/**
 * Telegram-based admin authentication
 *
 * Flow:
 * 1. Get initData from Telegram WebApp
 * 2. Send to backend /api/auth/session to get JWT
 * 3. Verify admin status via /api/user/is-admin
 * 4. Only allow access if backend confirms admin role
 */

const API_URL = process.env.REACT_APP_API_URL || 'https://api.kit8exchange.com';

export async function authenticateAdmin() {
  const tg = window.Telegram?.WebApp;

  if (!tg?.initData) {
    throw new Error('NO_TELEGRAM');
  }

  tg.ready();
  tg.expand();

  const user = tg.initDataUnsafe?.user;
  if (!user?.id) {
    throw new Error('NO_USER_DATA');
  }

  // Step 1: Create session via existing auth endpoint
  const sessionResponse = await fetch(`${API_URL}/api/auth/session`, {
    method: 'POST',
    credentials: 'omit',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      initData: tg.initData,
      userId: user.id,
      username: user.username || '',
      firstName: user.first_name || '',
      lastName: user.last_name || '',
      photoUrl: user.photo_url || '',
    }),
  });

  if (!sessionResponse.ok) {
    throw new Error('AUTH_FAILED');
  }

  const sessionData = await sessionResponse.json();
  if (!sessionData.success || !sessionData.sessionToken) {
    throw new Error('AUTH_FAILED');
  }

  const token = sessionData.sessionToken;
  const userId = String(user.id);

  // Step 2: Verify admin status on backend
  const adminResponse = await fetch(`${API_URL}/api/user/is-admin`, {
    method: 'GET',
    credentials: 'omit',
    headers: {
      'Authorization': `Bearer ${token}`,
      'X-User-ID': userId,
      'Content-Type': 'application/json',
    },
  });

  if (!adminResponse.ok) {
    throw new Error('NOT_ADMIN');
  }

  const adminData = await adminResponse.json();
  if (!adminData.success || !adminData.isAdmin) {
    throw new Error('NOT_ADMIN');
  }

  return {
    token,
    userId,
    user: {
      id: user.id,
      username: user.username,
      firstName: user.first_name,
      lastName: user.last_name,
      photoUrl: user.photo_url,
    },
  };
}
